/* =====================================================================
   Nala — Embroidery personalisation module
   ---------------------------------------------------------------------
   Ported from the embroidery-preview.html prototype. The canvas RENDER
   ENGINE (embroider / buildText) is kept verbatim in behaviour — it produces
   the stitched-thread look for TEXT. Icons now use the designer's pre-stitched
   "Embroidery Effect" PNGs (buildIcon draws them as-is). Everything else
   (config, state, UI wiring,
   cart logic) is rebuilt to theme conventions:

     - Config is read from a <script type="application/json"> block that
       Liquid renders (NOT hardcoded here).
     - On "Add to bag" the garment + a paid companion "Embroidery" product
       are added together in ONE /cart/add.js call, linked by a shared
       hidden `_embroideryRef` property.
     - Personalisation is captured as line-item properties on the garment.
   ===================================================================== */
(function () {
  'use strict';

  /* =====================================================================
     ENGINE — kept verbatim from the prototype (do not redesign)
     ===================================================================== */
  function hexToRgb(h) {
    h = h.replace('#', '');
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
  }
  function shade(hex, amt) { // amt -1..1
    const c = hexToRgb(hex);
    const f = (v) => Math.max(0, Math.min(255, Math.round(amt < 0 ? v * (1 + amt) : v + (255 - v) * amt)));
    return `rgb(${f(c.r)},${f(c.g)},${f(c.b)})`;
  }

  function embroider(off, unit) {
    const ctx = off.getContext('2d'), w = off.width, h = off.height;
    ctx.save();
    ctx.globalCompositeOperation = 'source-atop';

    // 1. satin-stitch lines — steep diagonal, alternating light/dark thread
    ctx.lineWidth = Math.max(1, unit * 0.05);
    const gap = Math.max(2.4, unit * 0.14), ang = -1.32, dx = Math.cos(ang), dy = Math.sin(ang);
    const diag = Math.hypot(w, h);
    let i = 0;
    for (let p = -diag; p < diag; p += gap) {
      ctx.strokeStyle = (i++ % 2 === 0) ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.13)';
      ctx.beginPath();
      ctx.moveTo(w / 2 + dx * p - dy * diag, h / 2 + dy * p + dx * diag);
      ctx.lineTo(w / 2 + dx * p + dy * diag, h / 2 + dy * p - dx * diag);
      ctx.stroke();
    }
    // 2. diagonal sheen
    const sh = ctx.createLinearGradient(0, 0, w, h);
    sh.addColorStop(0, 'rgba(255,255,255,0.24)');
    sh.addColorStop(.45, 'rgba(255,255,255,0)');
    sh.addColorStop(1, 'rgba(0,0,0,0.05)');
    ctx.fillStyle = sh; ctx.fillRect(0, 0, w, h);
    // 3. raised top-light / bottom-shade
    const ts = ctx.createLinearGradient(0, 0, 0, h);
    ts.addColorStop(0, 'rgba(255,255,255,0.16)');
    ts.addColorStop(.5, 'rgba(255,255,255,0)');
    ts.addColorStop(1, 'rgba(0,0,0,0.10)');
    ctx.fillStyle = ts; ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  function newOff(w, h) { const c = document.createElement('canvas'); c.width = Math.ceil(w); c.height = Math.ceil(h); return c; }

  // ---- TEXT -> embroidered offscreen ----
  function buildText(text, font, threadHex, fontSize) {
    const pad = fontSize * 0.55;
    const meas = newOff(10, 10).getContext('2d');
    meas.font = `${font.weight} ${fontSize}px ${font.css}`;
    const m = meas.measureText(text);
    const asc = m.actualBoundingBoxAscent || fontSize * 0.72, desc = m.actualBoundingBoxDescent || fontSize * 0.28;
    const w = Math.ceil(m.width) + pad * 2, h = Math.ceil(asc + desc) + pad * 2;
    const off = newOff(w, h), c = off.getContext('2d');
    c.font = `${font.weight} ${fontSize}px ${font.css}`;
    c.textBaseline = 'alphabetic';
    const x = pad, y = pad + asc;
    // base glyphs + a slightly darker edge for thread definition
    c.fillStyle = threadHex; c.fillText(text, x, y);
    c.lineWidth = Math.max(1, fontSize * 0.02); c.strokeStyle = shade(threadHex, -0.32); c.strokeText(text, x, y);
    embroider(off, fontSize);
    return { off, w, h };
  }

  // ---- ICON -> offscreen. Uses the designer's "Embroidery Effect" PNGs,
  // which already carry the stitched-thread texture, so we draw them as-is
  // (NO procedural embroider() pass — that would double the texture). `img`
  // is a preloaded HTMLImageElement; returns null until it has loaded.
  function buildIcon(img, size) {
    if (!img || !img.complete || !img.naturalWidth) return null;
    const off = newOff(size, size), c = off.getContext('2d');
    c.drawImage(img, 0, 0, size, size);
    return { off, w: size, h: size };
  }

  /* Icons are now the designer's "Embroidery Effect" PNGs (assets/
     embroidery-icon-*.png), loaded per-key and drawn as-is by buildIcon. */

  /* =====================================================================
     MODULE — config-driven UI + cart logic
     ===================================================================== */

  // Hardcoded prototype defaults — used whenever the Liquid config doesn't
  // supply a list, so threads / icons / fonts always render.
  const DEFAULT_THREADS = [
    { name: 'Black', hex: '#000000' },
    { name: 'Navy', hex: '#001044' },
    { name: 'Baby Blue', hex: '#91b7d6' },
    { name: 'Red', hex: '#960012' },
    { name: 'Pink', hex: '#e096aa' },
    { name: 'Purple', hex: '#af98e5' },
    { name: 'Gold', hex: '#d9a745' },
    { name: 'Silver', hex: '#b9bdc2' },
  ];
  // Fallback list only (the Liquid snippet supplies the real entries WITH the
  // `png` asset URLs). Icons render from the "Embroidery Effect" PNGs.
  const DEFAULT_ICONS = [
    { key: 'cherry', label: 'Cherry' },
    { key: 'stars', label: 'Stars' },
    { key: 'heart', label: 'Heart' },
    { key: 'bow', label: 'Bow' },
    { key: 'shell', label: 'Shell' },
    { key: 'flower', label: 'Flower' },
    { key: 'wings', label: 'Wings' },
    { key: 'star', label: 'Star' },
  ];
  const DEFAULT_FONTS = [
    { key: 'script', label: 'Roundhand', css: "'Nala Embroidery Script', cursive", weight: 700, scale: 1.0 },
    { key: 'block', label: 'Very Vogue', css: "'Nala Embroidery Block', Georgia, serif", weight: 400, scale: 0.92 },
  ];
  const DEFAULT_MAX_CHARS = 6;

  class EmbroideryModule {
    constructor(root) {
      this.root = root;
      this.config = this._readConfig();
      if (!this.config) return;

      // The canvas lives in the product media column (outside this root), so
      // it can replace the gallery and stay sticky — fall back to a local one.
      this.canvas = root.querySelector('[data-embroidery-canvas]') ||
        document.querySelector('[data-embroidery-canvas]');
      if (!this.canvas) return;

      const c = this.config;
      const d = c.defaults || {};
      this.aspect = c.previewAspect || 0.66;
      this.preview = this._resolvePreview(c);
      if (!this.preview) return; // no media tagged "embroider" → nothing to render
      this.opened = false;
      this.images = {}; // img url -> HTMLImageElement
      this.iconImages = {}; // icon key -> HTMLImageElement (Embroidery Effect PNG)
      this.iconThumbs = {}; // icon key -> thumbnail <canvas> (redrawn on load)
      this.state = {
        garment: this.preview,
        thread: c.threads[Math.min(d.thread || 0, c.threads.length - 1)] || c.threads[0],
        font: c.fonts[Math.min(d.font || 0, c.fonts.length - 1)] || c.fonts[0],
        type: d.type || 'text',
        text: '',
        icon: (c.icons[0] && c.icons[0].key) || 'heart',
      };
      // honour a configured default icon if present in the list
      if (d.icon && c.icons.some((i) => i.key === d.icon)) this.state.icon = d.icon;

      this._preloadImages();
      this._preloadIcons();
      this._cacheEls();
      this._buildUI();
      this._wireToggle();
      this._wirePreviewOnInput();
      this._wireMobileFloat();
      this._wireCart();
      this._wireTuner();
      this._wireColourSync();
      this._gateReady();
    }

    // Single preview garment, driven by the product media whose alt text
    // contains "embroider" (resolved server-side into config.previewImage).
    // Placement comes from a smart default, optionally overridden per image
    // via coords in the alt text (e.g. "embroider x=0.58 y=0.6 w=0.2 rot=-4").
    _resolvePreview(c) {
      if (!c.previewImage) return null;
      return { img: c.previewImage, place: this._parsePlace(c.previewAlt || '') };
    }

    // Parse optional placement coords out of the alt text; fall back to a
    // smart default. Coords are fractions of the drawn image rect. Since each
    // garment now has its own dedicated product, tune placement per product by
    // appending coords to the preview image's alt text, e.g.
    // "embroider x=.5 y=.4 w=.06 rot=-6".
    _parsePlace(alt) {
      // Smart defaults, resolved in order: per-product (PRODUCT_DEFAULTS,
      // keyed by product.handle — once a garment has been tuned live it's
      // baked in there) → per-category (DEFAULTS, tops sit higher on the
      // chest, underwear lower on the front) → underwear as the last resort.
      // `zoom` enlarges the garment in the canvas to trim the raw image's
      // whitespace (>1 = closer crop). `w` = stitch FONT SIZE as a fraction of
      // image width. Any value can be overridden per image via the alt text,
      // e.g. "embroider x=.5 y=.4 w=.06 rot=-6 zoom=1.3".
      // size/iconScale/iconPull are also tuned live (see _wireTuner) and baked
      // in per product/category here: size shrinks text+icon vs. w; iconScale
      // sizes the icon PNG (which carries its own big transparent margin, so
      // it needs a bigger build size than the text to read as the same visual
      // size); iconPull closes the visible text/icon gap by eating into that
      // same baked-in left margin (fraction of the icon's own width, 0–1)
      const DEFAULTS = {
        top:       { x: 0.615, y: 0.680, w: 0.050, rot: -2, zoom: 1.12, size: 0.82, iconScale: 1.60, iconPull: 0.10 },
        underwear: { x: 0.59, y: 0.340, w: 0.055, rot: -5, zoom: 1.30, size: 0.71, iconScale: 1.60, iconPull: 0.10 },
      };
      // Per-product overrides, keyed by a BASE fragment of product.handle (not
      // the full handle) — once a garment's placement has been tuned live
      // (Ctrl/Cmd+Shift+E) it's baked in here so it no longer depends on the
      // shared category default. Fragment matching (not exact) because each
      // colour of a garment is its own product/handle with a varying suffix,
      // e.g. "bio-high-waist-brief-licorice-personalise" and
      // "bio-high-waist-brief-personalise-blue-steel" both match the
      // "bio-high-waist-brief" fragment below. Anything not matched still
      // falls back to DEFAULTS[category] above.
      const PRODUCT_DEFAULTS = {
        'support-wirefree-bralette': { x: 0.615, y: 0.680, w: 0.050, rot: -2, zoom: 1.12, size: 0.82, iconScale: 1.60, iconPull: 0.10 },
        'bio-high-waist-brief': { x: 0.59, y: 0.340, w: 0.055, rot: -5, zoom: 1.30, size: 0.71, iconScale: 1.60, iconPull: 0.10 },
        'bio-high-waist-gee': { x: 0.630, y: 0.360, w: 0.054, rot: -11, zoom: 1.30, size: 0.63, iconScale: 1.60, iconPull: 0.10 },
        'padded-flexiwire-bra': { x: 0.625, y: 0.660, w: 0.052, rot: -2, zoom: 1.12, size: 0.74, iconScale: 1.80, iconPull: 0.10 },
      };
      // Longest fragment wins, so a more specific key (if one is ever added as
      // a substring of another, e.g. "bio-high-waist-brief" vs a hypothetical
      // "bio-high-waist-brief-xl") isn't shadowed by the shorter one.
      const handle = String(this.config.productHandle || '');
      const fragment = Object.keys(PRODUCT_DEFAULTS)
        .filter((k) => handle.indexOf(k) !== -1)
        .sort((a, b) => b.length - a.length)[0];
      const def = (fragment && PRODUCT_DEFAULTS[fragment]) || DEFAULTS[this.config.category] || DEFAULTS.underwear;
      const num = (k) => {
        const m = String(alt).match(new RegExp(k + '\\s*=\\s*(-?\\d*\\.?\\d+)'));
        return m ? parseFloat(m[1]) : null;
      };
      const pick = (v, d) => (v == null || isNaN(v)) ? d : v;
      return {
        x: pick(num('x'), def.x),
        y: pick(num('y'), def.y),
        w: pick(num('w'), def.w),
        rot: pick(num('rot'), def.rot),
        zoom: pick(num('zoom'), def.zoom),
        size: pick(num('size'), def.size),
        iconScale: pick(num('iconScale'), def.iconScale),
        iconPull: pick(num('iconPull'), def.iconPull),
      };
    }

    // Preload the initial garment photo plus every other colour's "embroider"
    // photo (config.previewImages), so switching colour swatches later swaps
    // the canvas instantly instead of waiting on a fresh image load.
    _preloadImages() {
      const initialUrl = this.preview && this.preview.img;
      const urls = new Set();
      if (initialUrl) urls.add(initialUrl);
      (this.config.previewImages || []).forEach((p) => { if (p && p.img) urls.add(p.img); });

      urls.forEach((url) => {
        if (this.images[url]) return;
        const im = new Image();
        im.onload = () => {
          // If the server didn't supply an aspect, derive it from the real image
          // so the canvas matches any crop ("looks good for every picture").
          if (url === initialUrl && (!this.config.previewAspect || this.config.previewAspect <= 0) && im.naturalWidth) {
            this.aspect = im.naturalWidth / im.naturalHeight;
          }
          this.render();
        };
        im.src = url;
        this.images[url] = im;
      });
    }

    /* ---- live colour sync: swap the canvas photo when the shopper picks a
       different colour swatch (multi-colour garments tag one "embroider"
       image per colour; single-colour garments only have one, so this is a
       no-op there). Matches the same alt-text convention `updateMediaByColor`
       uses in global.js: uppercase substring match of the colour value
       against the image's alt text. ---- */
    _getSelectedColourValue() {
      const input = document.querySelector(
        'input[type="radio"][name="Color"]:checked, input[type="radio"][name="Colour"]:checked',
      );
      if (input) return input.value.toUpperCase();
      const select = document.querySelector('select[name="Color"], select[name="Colour"]');
      if (select && select.value) return select.value.toUpperCase();
      return null;
    }

    _findImageForColourValue(colourUpper) {
      const list = this.config.previewImages || [];
      if (!colourUpper) return null;
      return list.find((p) => String((p && p.alt) || '').toUpperCase().includes(colourUpper)) || null;
    }

    _setGarmentImage(match) {
      const garment = { img: match.img, place: this._parsePlace(match.alt || '') };
      this.state.garment = garment;
      this.preview = garment;
      if (!this.images[match.img]) {
        const im = new Image();
        im.onload = () => this.render();
        im.src = match.img;
        this.images[match.img] = im;
      }
      this.render();
    }

    _wireColourSync() {
      if (!this.config.previewImages || this.config.previewImages.length < 2) return;
      document.addEventListener('change', () => {
        const colour = this._getSelectedColourValue();
        const match = this._findImageForColourValue(colour);
        if (!match || (this.state.garment && match.img === this.state.garment.img)) return;
        this._setGarmentImage(match);
      });
    }

    // Preload the icon PNGs (Embroidery Effect). On load, redraw the affected
    // thumbnail and the main preview so they appear as soon as they're ready.
    _preloadIcons() {
      (this.config.icons || []).forEach((ic) => {
        if (!ic || !ic.png || this.iconImages[ic.key]) return;
        const im = new Image();
        im.onload = () => {
          if (this.iconThumbs[ic.key]) this._drawIconThumb(this.iconThumbs[ic.key], ic.key);
          this.render();
        };
        im.src = ic.png;
        this.iconImages[ic.key] = im;
      });
    }

    _iconImg(key) { return this.iconImages[key]; }

    _readConfig() {
      const el = this.root.querySelector('[data-embroidery-config]');
      if (!el) return null;
      let cfg = {};
      try {
        cfg = JSON.parse(el.textContent) || {};
      } catch (e) {
        // Bad/partial config JSON — fall back entirely to hardcoded defaults
        // so the prototype still renders.
        console.warn('[embroidery] config JSON unparseable, using defaults', e);
        cfg = {};
      }
      // Always guarantee the lists are populated.
      cfg.threads = (cfg.threads && cfg.threads.length) ? cfg.threads : DEFAULT_THREADS;
      cfg.fonts = (cfg.fonts && cfg.fonts.length) ? cfg.fonts : DEFAULT_FONTS;
      cfg.icons = (cfg.icons && cfg.icons.length) ? cfg.icons : DEFAULT_ICONS;
      cfg.maxChars = cfg.maxChars || DEFAULT_MAX_CHARS;
      cfg.strings = cfg.strings || {};
      cfg.defaults = cfg.defaults || {};
      if (cfg.allowTypeChoice == null) cfg.allowTypeChoice = true;
      return cfg;
    }

    _cacheEls() {
      const r = this.root;
      this.els = {
        threads: r.querySelector('[data-embroidery-threads]'),
        threadName: r.querySelector('[data-embroidery-thread-name]'),
        fonts: r.querySelector('[data-embroidery-fonts]'),
        types: r.querySelector('[data-embroidery-types]'),
        textGroup: r.querySelector('[data-embroidery-text-group]'),
        textInput: r.querySelector('[data-embroidery-text]'),
        counter: r.querySelector('[data-embroidery-counter]'),
        iconGroup: r.querySelector('[data-embroidery-icon-group]'),
        icons: r.querySelector('[data-embroidery-icons]'),
        error: r.querySelector('[data-embroidery-error]'),
      };
    }

    /* ---- garment colour mapping + live sync to variant selection ---- */
    _currentVariantId() {
      const vs = document.querySelector('variant-selects, variant-radios');
      if (vs && vs.currentVariant && vs.currentVariant.id) return vs.currentVariant.id;
      const input = this._form() && this._form().querySelector('[name=id]');
      return input && input.value ? input.value : this.config.garmentVariantId;
    }

    _form() {
      if (this._formEl) return this._formEl;
      this._formEl =
        document.querySelector('product-form form') ||
        document.querySelector('form[action*="/cart/add"]');
      return this._formEl;
    }

    /* ---- UI construction ---- */
    _buildUI() {
      const { els, config: c, state } = this;

      // thread swatches
      els.threads.innerHTML = '';
      c.threads.forEach((t, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'embroidery__swatch';
        b.style.background = t.hex;
        b.title = t.name;
        b.setAttribute('aria-label', 'Thread ' + t.name);
        b.setAttribute('aria-pressed', state.thread === t ? 'true' : 'false');
        b.addEventListener('click', () => {
          state.thread = t;
          if (els.threadName) els.threadName.textContent = t.name;
          [...els.threads.children].forEach((el, j) => el.setAttribute('aria-pressed', j === i ? 'true' : 'false'));
          this.render();
        });
        els.threads.appendChild(b);
      });
      if (els.threadName) els.threadName.textContent = state.thread.name;

      // fonts
      if (els.fonts) {
        els.fonts.innerHTML = '';
        if (c.fonts.length < 2) els.fonts.closest('[data-embroidery-font-group]')?.setAttribute('hidden', '');
        c.fonts.forEach((f, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'embroidery__pill embroidery__pill--font';
          b.textContent = f.label;
          b.style.fontFamily = f.css;
          b.style.fontWeight = f.weight;
          b.setAttribute('aria-pressed', state.font === f ? 'true' : 'false');
          b.addEventListener('click', () => {
            state.font = f;
            [...els.fonts.children].forEach((el, j) => el.setAttribute('aria-pressed', j === i ? 'true' : 'false'));
            this.render();
          });
          els.fonts.appendChild(b);
        });
      }

      // type toggle (text / icon / both)
      const TYPES = [['text', 'Text'], ['icon', 'Icon'], ['both', 'Text + Icon']];
      if (els.types) {
        els.types.innerHTML = '';
        if (!c.allowTypeChoice) {
          els.types.closest('[data-embroidery-type-group]')?.setAttribute('hidden', '');
        } else {
          TYPES.forEach(([key, label]) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'embroidery__pill';
            b.textContent = label;
            b.dataset.type = key;
            b.setAttribute('aria-pressed', state.type === key ? 'true' : 'false');
            b.addEventListener('click', () => {
              state.type = key;
              [...els.types.children].forEach((el) => el.setAttribute('aria-pressed', el.dataset.type === key ? 'true' : 'false'));
              this._toggleFields();
              this._clearError();
              this.render();
            });
            els.types.appendChild(b);
          });
        }
      }

      // icon pills — each shows the actual icon artwork on a small canvas
      if (els.icons) {
        els.icons.innerHTML = '';
        c.icons.forEach((ic) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'embroidery__icon-pill';
          b.title = ic.label;
          b.setAttribute('aria-label', ic.label);
          b.setAttribute('aria-pressed', state.icon === ic.key ? 'true' : 'false');

          const thumb = document.createElement('canvas');
          thumb.className = 'embroidery__icon-thumb';
          this.iconThumbs[ic.key] = thumb; // so _preloadIcons can redraw on load
          this._drawIconThumb(thumb, ic.key);
          b.appendChild(thumb);

          const lbl = document.createElement('span');
          lbl.className = 'embroidery__icon-pill-label';
          lbl.textContent = ic.label;
          b.appendChild(lbl);

          b.addEventListener('click', () => {
            state.icon = ic.key;
            [...els.icons.children].forEach((el) => el.setAttribute('aria-pressed', el === b ? 'true' : 'false'));
            this.render();
          });
          els.icons.appendChild(b);
        });
      }

      // text input
      if (els.textInput) {
        els.textInput.maxLength = c.maxChars;
        els.textInput.value = state.text;
        if (els.counter) els.counter.textContent = `${state.text.length}/${c.maxChars}`;
        els.textInput.addEventListener('input', () => {
          state.text = els.textInput.value.slice(0, c.maxChars);
          if (els.counter) els.counter.textContent = `${state.text.length}/${c.maxChars}`;
          this._clearError();
          this.render();
        });
      }

      this._toggleFields();
    }

    _toggleFields() {
      const { els, state } = this;
      if (els.textGroup) els.textGroup.hidden = state.type === 'icon';
      if (els.iconGroup) els.iconGroup.hidden = state.type === 'text';
    }

    // Draw an icon's PNG into a small swatch canvas for the icon pills.
    _drawIconThumb(cv, key) {
      const size = 58;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = size * dpr;
      cv.height = size * dpr;
      const ctx = cv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size, size);
      const img = this._iconImg(key);
      if (img && img.complete && img.naturalWidth) ctx.drawImage(img, 0, 0, size, size);
    }

    // Fit an image inside the canvas, preserving aspect ratio (object-fit: contain).
    _containRect(cw, ch, iw, ih) {
      const ar = iw / ih;
      let w = cw, h = cw / ar;
      if (h > ch) { h = ch; w = ch * ar; }
      return { x: (cw - w) / 2, y: (ch - h) / 2, w, h };
    }

    /* ---- main render: garment photo + embroidery on its placement spot ---- */
    render() {
      const { canvas, state } = this;
      const ctx = canvas.getContext('2d');
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssW = canvas.clientWidth || 440, cssH = cssW / this.aspect;
      canvas.style.height = cssH + 'px';
      this._lastCssH = cssH;
      canvas.width = cssW * dpr; canvas.height = cssH * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cssW, cssH);

      // Draw the garment photo (contain, then zoom to trim whitespace). `r` =
      // the drawn image rect; placement fractions are relative to it, so the
      // stitch stays locked to the garment as it scales.
      const g = state.garment;
      const place = (g && g.place) || { x: 0.5, y: 0.45, w: 0.06, rot: 0, size: 1, iconScale: 1.7, iconPull: 0 };
      const img = g && this.images[g.img];
      let r = { x: 0, y: 0, w: cssW, h: cssH };
      if (img && img.complete && img.naturalWidth) {
        r = this._containRect(cssW, cssH, img.naturalWidth, img.naturalHeight);
        const zoom = place.zoom || 1;
        if (zoom !== 1) {
          const midX = r.x + r.w / 2, midY = r.y + r.h / 2;
          r.w *= zoom; r.h *= zoom;
          r.x = midX - r.w / 2; r.y = midY - r.h / 2;
        }
        ctx.drawImage(img, r.x, r.y, r.w, r.h);
      }

      const cx = r.x + place.x * r.w;
      const cy = r.y + place.y * r.h;
      const SIZE_ADJUST = place.size || 1;

      const wantText = state.type !== 'icon' && state.text.trim().length > 0;
      const wantIcon = state.type !== 'text';

      // Empty state: show a faded sample stitch at the real placement spot so
      // the customer instantly sees where/how the embroidery will sit. Drawn
      // with the actual engine (thread colour + scale + rotation), just ghosted.
      if (!wantText && !wantIcon) {
        const sample = this.config.strings.sample || 'Text';
        const REF = 150;
        const fontPx = REF * (state.font.scale || 1);
        const ghost = buildText(sample, state.font, state.thread.hex, fontPx);
        const scale = (place.w * r.w * SIZE_ADJUST) / fontPx; // same fixed size as the real render
        ctx.save();
        ctx.globalAlpha = 0.3;
        ctx.translate(cx, cy);
        ctx.rotate((place.rot || 0) * Math.PI / 180);
        ctx.drawImage(ghost.off, -ghost.w * scale / 2, -ghost.h * scale / 2, ghost.w * scale, ghost.h * scale);
        ctx.restore();
        return;
      }

      // Build the embroidery artwork at a fixed reference size.
      const REF = 150;
      const fontPx = REF * (state.font.scale || 1);
      const textObj = wantText ? buildText(state.text, state.font, state.thread.hex, fontPx) : null;
      // Icon PNG already carries its stitch texture; roughly half the square is
      // transparent padding baked into the artwork, so it needs a bigger build
      // size than the text's fontPx to read as the same visual size.
      // `place.iconScale` (live-tunable, see _wireTuner) controls that build size.
      const iconObj = wantIcon ? buildIcon(this._iconImg(state.icon), REF * (place.iconScale || 1.7)) : null;

      // Consistent stitch size: the text renders at a FIXED on-image font size
      // (place.w = font size as a fraction of image width), so adding characters
      // makes the word WIDER rather than shrinking each letter. No auto-fit.
      const scale = (place.w * r.w * SIZE_ADJUST) / fontPx;
      // The icon is built at a fixed REF-based size (not fontPx-based — see
      // buildIcon call above), so it needs its OWN scale keyed to REF instead
      // of fontPx. Reusing the text `scale` here would make the icon's on-screen
      // size ride on `font.scale` (different fonts have different `scale` values
      // to normalize their metrics) even though nothing about the icon changed —
      // e.g. switching to a font with scale 0.92 would inflate the icon ~8.7%.
      const iconDrawScale = (place.w * r.w * SIZE_ADJUST) / REF;
      // The text canvas carries transparent padding (buildText: pad = fontPx*0.55)
      // on each side. Tuck the icon back into that trailing padding so text and
      // icon sit close together.
      const tuck = (wantText && wantIcon) ? fontPx * 0.55 * 0.9 * scale : 0;
      const iconPxW = iconObj ? iconObj.w * iconDrawScale : 0;
      const iconPxH = iconObj ? iconObj.h * iconDrawScale : 0;
      // The icon PNG's own baked-in left margin (see comment above buildIcon) is
      // what actually dominates the remaining gap. `place.iconPull` pulls the
      // icon's bounding box left by a fraction of its own width, eating into
      // that margin without clipping the artwork (it's transparent).
      const iconPull = (wantText && wantIcon && iconObj) ? iconPxW * (place.iconPull || 0) : 0;
      const textPxW = textObj ? textObj.w * scale : 0;
      const totalPxW = textPxW - tuck + (iconPxW - iconPull);

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate((place.rot || 0) * Math.PI / 180);
      ctx.shadowColor = 'rgba(0,0,0,0.34)';
      ctx.shadowBlur = REF * scale * 0.10;
      ctx.shadowOffsetY = REF * scale * 0.05;
      let dx = -totalPxW / 2;
      if (textObj) {
        ctx.drawImage(textObj.off, dx, -textObj.h * scale / 2, textPxW, textObj.h * scale);
        dx += textPxW - tuck;
      }
      if (iconObj) {
        ctx.drawImage(iconObj.off, dx - iconPull, -iconPxH / 2, iconPxW, iconPxH);
      }
      ctx.restore();
    }

    /* ---- toggle: open by default; just collapses/expands the form body ---- */
    _wireToggle() {
      const btn = this.root.querySelector('[data-embroidery-toggle]');
      const body = this.root.querySelector('[data-embroidery-body]');

      // Label swaps with state: "REMOVE EMBROIDERY" when open (it collapses the
      // module), "Personalise with embroidery" when closed. Strings come from
      // the button's data-label-* attributes so Liquid stays the source of copy.
      const labelEl = btn && btn.querySelector('.embroidery__toggle-label');
      const syncLabel = () => {
        if (!labelEl) return;
        const key = this.opened ? 'labelOpen' : 'labelClosed';
        if (btn.dataset[key]) labelEl.textContent = btn.dataset[key];
      };

      // Open by default: on this template the personalisation IS the product.
      this.opened = true;
      if (btn) btn.setAttribute('aria-expanded', 'true');
      if (body) body.hidden = false;
      syncLabel();

      if (btn && body) {
        btn.addEventListener('click', () => {
          this.opened = !this.opened;
          btn.setAttribute('aria-expanded', this.opened ? 'true' : 'false');
          body.hidden = !this.opened;
          syncLabel();
        });
      }
    }

    // Jump the gallery to the live-canvas slide the moment the customer makes
    // ANY embroidery selection (typing text, or picking type / icon / thread /
    // font). The canvas is a normal slide in the product gallery, so it shares
    // the exact same arrows / thumbnails / swipe as the photos — to the shopper
    // it is just another gallery image they can page to and away from freely.
    _wirePreviewOnInput() {
      const slide = this.canvas && this.canvas.closest('[data-media-id]');
      this.canvasMediaId = slide ? slide.getAttribute('data-media-id') : null;
      if (!this.canvasMediaId) return;

      // media-gallery is defer-loaded, so resolve it at CALL time.
      const toCanvas = () => {
        const g = document.querySelector('media-gallery');
        if (g && typeof g.setActiveMedia === 'function') g.setActiveMedia(this.canvasMediaId, false);
        requestAnimationFrame(() => this.render()); // crisp draw once the slide is on-screen
      };
      const text = this.root.querySelector('[data-embroidery-text]');
      if (text) text.addEventListener('input', toCanvas);
      this.root.addEventListener('click', (e) => {
        if (e.target.closest(
          '[data-embroidery-threads] button,[data-embroidery-icons] button,' +
          '[data-embroidery-types] button,[data-embroidery-fonts] button'
        )) toCanvas();
      });
    }

    // Mobile "sticky", but ONLY for the live-canvas slide (not the photos):
    // position:sticky is unreliable inside this theme's product flex layout, so
    // we pin in JS. When the canvas is the slide in view AND the gallery scrolls
    // above the viewport top, fix it as a card at the top (.is-embroidery-float)
    // and reserve its space with a spacer; scrolling back — or swiping to a
    // photo — releases it. Desktop uses real CSS sticky and this stays inert.
    _wireMobileFloat() {
      const gallery = document.querySelector('media-gallery');
      if (!gallery || !gallery.parentNode) return;
      const viewer = gallery.querySelector('slider-component[id^="GalleryViewer"]') ||
        gallery.querySelector('slider-component');
      // The theme gives .product__media-wrapper `backface-visibility:hidden`,
      // which creates a stacking context that would trap our fixed card BELOW
      // the controls column. We lift that whole layer while floating.
      const wrapper = gallery.closest('.product__media-wrapper');

      const spacer = document.createElement('div');
      spacer.className = 'embroidery-float-spacer';
      spacer.hidden = true;
      gallery.parentNode.insertBefore(spacer, gallery);

      let floating = false;
      let onCanvas = false; // is the live-canvas slide the one currently in view?

      // "Personalise me" badge overlays the gallery, but shouldn't show while
      // the live-canvas slide itself is in view (redundant there).
      const setBadgeHidden = (hidden) => {
        gallery.classList.toggle('is-embroidery-canvas-active', hidden);
      };

      // First non-canvas slide id, so we can snap back to the product gallery.
      const firstPhotoId = () => {
        const items = gallery.querySelectorAll('.product__media-item[data-media-id]');
        for (let i = 0; i < items.length; i++) {
          const id = items[i].getAttribute('data-media-id');
          if (id && id !== this.canvasMediaId) return id;
        }
        return null;
      };
      const backToGallery = () => {
        const id = firstPhotoId();
        if (id && typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(id, false);
      };

      const setFloat = (on) => {
        if (on === floating) return;
        const wasFloating = floating;
        floating = on;
        if (on) {
          spacer.style.height = gallery.offsetHeight + 'px';
          spacer.hidden = false;
          gallery.classList.add('is-embroidery-float');
          if (wrapper) wrapper.classList.add('is-embroidery-float-host');
        } else {
          gallery.classList.remove('is-embroidery-float');
          if (wrapper) wrapper.classList.remove('is-embroidery-float-host');
          spacer.hidden = true;
          spacer.style.height = '';
          // Released by scrolling back up while still on the canvas → return to
          // the normal gallery view (first photo). The canvas stays a slide the
          // shopper can swipe to, and any new selection jumps back to it.
          if (wasFloating && onCanvas) backToGallery();
        }
        requestAnimationFrame(() => this.render());
      };

      const onScroll = () => {
        if (!onCanvas || !window.matchMedia('(max-width: 749px)').matches) { setFloat(false); return; }
        // At the very top → never pinned; show the normal gallery.
        if (window.scrollY <= 0) { setFloat(false); return; }
        // While floating the gallery is fixed, so measure the in-flow spacer;
        // otherwise measure the gallery itself. Pin once its top passes 0.
        const ref = floating ? spacer : gallery;
        setFloat(ref.getBoundingClientRect().top <= 0);
      };

      // Track the active slide (fires on swipe AND arrow/thumbnail nav).
      if (viewer) {
        viewer.addEventListener('slideChanged', (e) => {
          const el = e.detail && e.detail.currentElement;
          onCanvas = !!(el && el.getAttribute('data-media-id') === this.canvasMediaId);
          setBadgeHidden(onCanvas);
          onScroll();
        });
      }

      // Initial state — the canvas slide isn't necessarily first, but cover
      // the case where it's already active on load (e.g. restored via URL).
      const activeEl = gallery.querySelector('[data-media-id].is-active');
      onCanvas = !!(activeEl && activeEl.getAttribute('data-media-id') === this.canvasMediaId);
      setBadgeHidden(onCanvas);

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();
    }

    // Dev-only placement tuner. Toggle it ANY TIME (no refresh) with
    // Ctrl/Cmd + Shift + E, or auto-open by appending ?embdebug (or ?debug) to
    // the PDP URL. Live x / y / w / rot controls for the embroidery placement:
    // x,y = the artwork CENTRE as a fraction of the drawn image (0–1); w =
    // stitch font size as a fraction of image width; rot = degrees. Adjust
    // until it sits right, hit "Copy", then bake the snippet into
    // _parsePlace()'s `def` or the preview image's alt text.
    // Below that, a second "size / gap" group tunes more `place` fields, same
    // per-category scope as x/y/w/rot/zoom: `size` shrinks text+icon vs. w;
    // `iconScale` sizes the icon's own build box (raise/lower until the icon
    // reads the same visual size as the text — the PNG's baked-in padding
    // means the box has to be bigger than fontPx to look comparable);
    // `iconPull` closes the visible gap by eating into that same baked-in
    // left padding (it's transparent, so nothing gets clipped). Bake final
    // values into DEFAULTS in _parsePlace(). Never shown to customers unless
    // they know the shortcut/flag.
    _wireTuner() {
      const q = new URLSearchParams(location.search);
      if (q.has('embdebug') || q.has('debug')) this._toggleTuner(true);

      document.addEventListener('keydown', (e) => {
        // Ctrl/Cmd + Shift + E — a combo that won't fire while typing a name.
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'E' || e.key === 'e')) {
          e.preventDefault();
          this._toggleTuner();
        }
      });
    }

    _toggleTuner(forceOpen) {
      if (this._tuner) {
        if (forceOpen) return;               // already open
        this._tuner.remove();
        this._tuner = null;
        return;
      }
      const place = this.state.garment && this.state.garment.place;
      if (!place) return;

      const FIELDS = [
        { k: 'x',    min: 0,    max: 1,    step: 0.005, dp: 3 },
        { k: 'y',    min: 0,    max: 1,    step: 0.005, dp: 3 },
        { k: 'w',    min: 0.01, max: 0.30, step: 0.002, dp: 3 },
        { k: 'rot',  min: -45,  max: 45,   step: 1,     dp: 0 },
        { k: 'zoom', min: 1,    max: 2,    step: 0.02,  dp: 2 },
      ];
      // Per category (same `place` object as above) — text size, icon size,
      // and the gap between them. `iconScale` sizes the icon's own build box
      // (bigger vs. text because the PNG carries a big transparent margin);
      // `iconPull` is what actually closes the visible gap.
      const SIZE_FIELDS = [
        { k: 'size',      label: 'size',     min: 0.5, max: 1.2, step: 0.01, dp: 2 },
        { k: 'iconScale', label: 'iconSize', min: 0.5, max: 3.0, step: 0.05, dp: 2 },
        { k: 'iconPull',  label: 'iconPull', min: 0,   max: 0.6, step: 0.01, dp: 2 },
      ];

      const panel = document.createElement('div');
      panel.setAttribute('data-embroidery-tuner', '');
      this._tuner = panel;
      panel.style.cssText = 'position:fixed;top:12px;left:12px;z-index:2147483647;' +
        'background:rgba(20,20,20,0.92);color:#fff;font:12px/1.4 monospace;' +
        'padding:12px 14px;border-radius:8px;width:270px;box-shadow:0 6px 24px rgba(0,0,0,0.35)';

      const title = document.createElement('div');
      title.textContent = 'Embroidery placement';
      title.style.cssText = 'font-weight:bold;margin-bottom:8px;padding-right:16px';
      const close = document.createElement('button');
      close.type = 'button'; close.textContent = '×';
      close.title = 'Close (Ctrl/Cmd+Shift+E)';
      close.style.cssText = 'position:absolute;top:6px;right:8px;background:none;border:0;color:#fff;font-size:16px;line-height:1;cursor:pointer';
      close.addEventListener('click', () => this._toggleTuner());
      panel.appendChild(title);
      panel.appendChild(close);

      const snippet = document.createElement('code');
      snippet.style.cssText = 'display:block;margin:8px 0;color:#8fe3a0;word-break:break-all';

      const fmt = () => 'embroider ' + FIELDS.concat(SIZE_FIELDS).map((f) => `${f.k}=${(+place[f.k]).toFixed(f.dp)}`).join(' ');
      const rerender = () => { snippet.textContent = fmt(); this.render(); };

      // Shared row builder — both groups are bound to the same `place` object.
      const addRow = (container, target, f) => {
        const row = document.createElement('label');
        row.style.cssText = 'display:flex;align-items:center;gap:6px;margin:4px 0';
        const name = document.createElement('span');
        name.textContent = f.label || f.k;
        name.style.cssText = 'width:52px;flex:none';
        const range = document.createElement('input');
        range.type = 'range'; range.min = f.min; range.max = f.max; range.step = f.step;
        range.value = target[f.k]; range.style.flex = '1';
        const numb = document.createElement('input');
        numb.type = 'number'; numb.min = f.min; numb.max = f.max; numb.step = f.step;
        numb.value = target[f.k];
        numb.style.cssText = 'width:58px;background:#000;color:#fff;border:1px solid #555;border-radius:4px;padding:2px 4px';
        const set = (v) => {
          v = parseFloat(v);
          if (isNaN(v)) return;
          target[f.k] = v; range.value = v; numb.value = v; rerender();
        };
        range.addEventListener('input', () => set(range.value));
        numb.addEventListener('input', () => set(numb.value));
        row.append(name, range, numb);
        container.appendChild(row);
      };

      FIELDS.forEach((f) => addRow(panel, place, f));

      const sizeTitle = document.createElement('div');
      sizeTitle.textContent = 'Text + icon size / gap';
      sizeTitle.style.cssText = 'font-weight:bold;margin:10px 0 4px;border-top:1px solid #444;padding-top:8px';
      panel.appendChild(sizeTitle);
      SIZE_FIELDS.forEach((f) => addRow(panel, place, f));

      panel.appendChild(snippet);

      const copy = document.createElement('button');
      copy.type = 'button'; copy.textContent = 'Copy snippet';
      copy.style.cssText = 'width:100%;background:#2f7;color:#000;border:0;border-radius:4px;padding:5px;font:inherit;cursor:pointer';
      copy.addEventListener('click', () => {
        if (navigator.clipboard) navigator.clipboard.writeText(fmt());
        copy.textContent = 'Copied ✓';
        setTimeout(() => { copy.textContent = 'Copy snippet'; }, 1200);
      });
      panel.appendChild(copy);

      document.body.appendChild(panel);
      rerender();
    }

    /* ---- "active" = panel opened AND there is something to embroider ---- */
    _isActive() {
      const wantText = this.state.type !== 'icon' && this.state.text.trim().length > 0;
      const wantIcon = this.state.type !== 'text';
      return this.opened && (wantText || wantIcon);
    }

    _showError(msg) {
      if (!this.els.error) return;
      this.els.error.textContent = msg;
      this.els.error.hidden = false;
    }
    _clearError() {
      if (this.els.error) { this.els.error.hidden = true; this.els.error.textContent = ''; }
    }

    /* ---- cart: intercept the add-to-cart submit when embroidery is on ---- */
    _wireCart() {
      // Document-level capture listener: guaranteed to run during the capture
      // phase, i.e. BEFORE the theme's <product-form> handler (which is bound
      // to the form in the target/bubble phase). stopImmediatePropagation()
      // then prevents the theme from doing its own single-line add when we
      // take over. (A listener on the form node itself can't guarantee order
      // at the target.) Matches any /cart/add form on the page so the sticky
      // add-to-cart bar is covered too.
      document.addEventListener('submit', (evt) => {
        const form = evt.target;
        if (!(form instanceof HTMLFormElement)) return;
        if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;

        // This is the dedicated embroidery template — personalisation is
        // REQUIRED, not optional (orders were coming in with the garment
        // added but no text/icon chosen). Block the add until something's
        // been personalised.
        if (!this._isActive()) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this._showError(this.config.strings.errorText || 'Please add your embroidery text, or switch to an icon.');
          return;
        }

        const variantId = this._currentVariantId();
        const vs = document.querySelector('variant-selects, variant-radios');
        const noVariant = vs && (!vs.currentVariant || vs.currentVariant.available === false);

        // Block only if no size chosen.
        if (noVariant) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this._showError(this.config.strings.errorSize || 'Please select your size before adding to your cart.');
          return;
        }

        evt.preventDefault();
        evt.stopImmediatePropagation();
        this._addToCart(form, variantId);
      }, true);
    }

    _addToCart(form, variantId) {
      const { state, config } = this;
      const qtyInput = form.querySelector('[name=quantity]');
      const qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

      // Pricing model: the embroidery cost is baked into each garment product's
      // own price (no separate add-on line item). We only attach the chosen
      // personalisation to the single garment line as line-item properties.
      // (The old companion add-on approach is archived at
      // docs/embroidery/ARCHIVED-addon-lineitem-approach.md.)
      //
      // `_embroideryRef` is still written (unique per add) for two reasons even
      // without a second line: (1) cart-drawer-items.liquid renders the
      // personalisation only on lines that carry it, and (2) it stops Shopify
      // from merging two differently-personalised units of the same variant.
      const props = {
        'Font style': state.font.label,
        'Thread colour': state.thread.name,
        '_embroideryRef': this._makeRef(),
      };
      if (state.type !== 'icon' && state.text.trim()) props['Text'] = state.text.trim();
      if (state.type !== 'text') {
        const ic = config.icons.find((i) => i.key === state.icon);
        props['Icon'] = (ic && ic.label) || state.icon;
      }

      const items = [
        { id: Number(variantId), quantity: qty, properties: props },
      ];

      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      const body = { items };
      // Bundled section rendering so we can refresh the drawer in one round-trip.
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((s) => s.id).join(',');
        body.sections_url = window.location.pathname;
      }

      this._setSubmitting(true);
      this._clearError();

      fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.status) {
            this._showError(res.description || 'Something went wrong adding to your cart. Please try again.');
            return;
          }
          if (cart && typeof cart.renderContents === 'function' && res.sections) {
            cart.renderContents(res);
          } else if (cart && typeof cart.open === 'function') {
            cart.open();
            window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: res } }));
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch((e) => {
          console.error('[embroidery] add failed', e);
          this._showError('Something went wrong adding to your cart. Please try again.');
        })
        .finally(() => this._setSubmitting(false));
    }

    _makeRef() {
      try {
        if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
      } catch (e) { /* fall through */ }
      return 'emb-' + Math.abs((performance.now() * 1e6) | 0).toString(36) + '-' +
        ([1e7] + -1e3).replace(/[018]/g, () => ((Math.random() * 16) | 0).toString(16));
    }

    _setSubmitting(on) {
      const btn = document.querySelector('#AddToCart');
      if (btn) btn.setAttribute('aria-disabled', on ? 'true' : 'false');
      this.root.classList.toggle('is-submitting', !!on);
    }

    /* ---- font gate (never draw with a fallback face) ---- */
    _gateReady() {
      const families = this.config.fonts
        .map((f) => `${f.weight || 600} 24px ${f.css}`)
        .filter(Boolean);

      const drawOnce = () => this.render();

      const ready = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
      // Explicitly request each face so document.fonts.ready waits on them.
      if (document.fonts && families.length) {
        families.forEach((spec) => { try { document.fonts.load(spec); } catch (e) { /* noop */ } });
      }
      ready.then(drawOnce);
      setTimeout(drawOnce, 700); // safety net if a face is blocked/missing

      // keep the preview crisp on resize / orientation change
      let raf = null;
      window.addEventListener('resize', () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(drawOnce);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-embroidery-root]').forEach((root) => {
      if (root.dataset.embroideryInit) return;
      root.dataset.embroideryInit = '1';
      new EmbroideryModule(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init after Shopify theme-editor section reloads.
  document.addEventListener('shopify:section:load', init);
})();
